import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Switch,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { useDebounce } from "../hooks/useDebounce";
import {
  fetchCategories,
  fetchCategoryStatistics,
} from "../store/slices/categoriesSlice";
import EnhancedTable from "../components/common/EnhancedTable";
import CategoryDialog from "../components/dialogs/CategoryDialog";
import DeleteConfirmDialog from "../components/dialogs/DeleteConfirmDialog";
import { categoryAPI } from "../services/api";
import StatusChip from "../utils/StatusChip";
import ExportImportButtons from "../components/common/ExportImportButtons";

interface CategoryMedia {
  id: string;
  categoryname: string;
  createdby: string;
  modifiedby: string | null;
  masterdetailId: string;
  categoryId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
}

interface Category {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
  categorymedia: CategoryMedia[];
  isShow: boolean; // <-- change from number to boolean if needed
}

interface CategoryFormData {
  id?: string;
  name: string;
  status: string;
  image?: File | string;
}

const CategoriesList: React.FC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { categories, loading, statistics, statisticsLoading, pagination } =
    useAppSelector((state: any) => state.categories);

  const [columnFilters, setColumnFilters] = useState({
  name: "",
  categorystatus: "all",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryFormData | null>(null);
  const [dialogMode, setDialogMode] = useState<"add" | "edit" | "view">("add");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false); // <-- add this

  // Column reordering state
  const [columns, setColumns] = useState([
    {
      id: "name",
      label: "Category Name",
      minWidth: 200,
      sortable: true,
      draggable: true,
      format: (value: string, row: Category) => {
        const imageUrl = row.categorymedia?.[0]?.categoryname
          ? categoryAPI.getMediaDownloadUrl(row.categorymedia[0].categoryname)
          : undefined;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={imageUrl}
              alt={value}
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.main",
                fontSize: "1rem",
                fontWeight: "bold",
                border: "2px solid",
                borderColor: "primary.light",
              }}
              onError={(e) => {
                console.log("Image load error for:", imageUrl);
                // Just let the avatar show the letter if image fails
              }}
            >
              {value.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: "text.secondary" }}
            >
              {value}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "totalsubcategories",
      label: "Total Active Subcategories",
      minWidth: 160,
      align: "center" as const,
      draggable: true,
      sortable : true,
      format: (value: number) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {value || 0}
        </Typography>
      ),
    },
    {
      id: "categorystatus",
      label: "Status",
      minWidth: 120,
      align: "center" as const,
      draggable: true,
      format: (value: number) => <StatusChip status={value} />,
    },
    {
      id: "isShow",
      label: "Dashboard Show",
      minWidth: 120,
      align: "center" as const,
      draggable: true,
      format: (value: boolean, row: Category) => (
        <Switch
          checked={!!value}
          color="primary"
          onChange={() => handleDashboardShowToggle(row)}
          inputProps={{ "aria-label": "dashboard show toggle" }}
        />
      ),
    },
  ]);

  // Ref to track if column order has been initialized
  const isInitialized = React.useRef(false);

  const debouncedCategoryName = useDebounce(columnFilters.name, 500);
  // removed totalsubcategories filter debounce

  const handleColumnReorder = (newColumns: any[]) => {
    setColumns(newColumns);
    localStorage.setItem(
      "categoriesTableColumnOrder",
      JSON.stringify(newColumns.map((col: any) => col.id))
    );
  };

  // Load saved column order on component mount
  React.useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedColumnOrder = localStorage.getItem("categoriesTableColumnOrder");
    if (savedColumnOrder) {
      try {
        const savedIds = JSON.parse(savedColumnOrder);
        const reorderedColumns = savedIds
          .map((id: string) => columns.find((col) => col.id === id))
          .filter(Boolean);

        const existingIds = reorderedColumns.map((col: any) => col.id);
        const newColumns = columns.filter(
          (col: any) => !existingIds.includes(col.id)
        );

        setColumns([...reorderedColumns, ...newColumns]);
      } catch (error) {
        console.error("Error loading saved column order:", error);
      }
    }
  }, [columns]); // Now safe to include 'columns' in dependency array

  const loadCategories = useCallback(() => {
    const params: any = {
      page: currentPage,
      items_per_page: itemsPerPage,
    };

    if (debouncedCategoryName.trim()) {
      params["search"] = debouncedCategoryName.trim();
    }
  // removed totalsubcategories filter logic
    if (
      columnFilters.categorystatus &&
      columnFilters.categorystatus !== "all"
    ) {
      params["where[categorystatus]"] =
        columnFilters.categorystatus === "active" ? 1 : 0;
    }

    // Add search parameter if provided

    // Add status filter if not 'all'

    if (sortField) {
      params.sort = `${sortField} ${sortDirection}`;
    }

    dispatch(fetchCategories(params));
  }, [
    dispatch,
    currentPage,
    itemsPerPage,
    // debouncedSearchTerm,
    // statusFilter,
    sortField,
    sortDirection,
    columnFilters,
    debouncedCategoryName,
  // removed totalsubcategories filter dependency
  ]);

  useEffect(() => {
    dispatch(fetchCategoryStatistics());
  }, [dispatch]);

  useEffect(() => {
    if (currentPage > 0 && itemsPerPage > 0) {
        const params: any = {
      page: currentPage,
      items_per_page: itemsPerPage,
    };

    if (debouncedCategoryName.trim()) {
      params["search"] = debouncedCategoryName.trim();
    }
  // removed totalsubcategories filter logic
    if (
      columnFilters.categorystatus &&
      columnFilters.categorystatus !== "all"
    ) {
      params["where[categorystatus]"] =
        columnFilters.categorystatus === "active" ? 1 : 0;
    }

    // Add search parameter if provided

    // Add status filter if not 'all'

    if (sortField) {
      params.sort = `${sortField} ${sortDirection}`;
    }

    dispatch(fetchCategories(params));
    }
  }, [
    // loadCategories,
    dispatch,
    debouncedCategoryName,
    columnFilters.categorystatus,
    currentPage,
    itemsPerPage,
    sortField,
    sortDirection,
  ]);

  const handleColumnFilterChange = (columnId: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Debug: Log the categories state
  useEffect(() => {
    // Debug image URLs - removed unused imageUrl variable
    if (Array.isArray(categories)) {
      categories.forEach((category) => {
        if (category.categorymedia?.[0]?.categoryname) {
          // Image URL is available for debugging if needed
        }
      });
    }
  }, [categories]);

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setDialogMode("add");
    setDialogOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setSelectedCategory({
      id: category.id,
      name: category.name,
      status: category.categorystatus === 1 ? "1" : "0",
      image: category.categorymedia?.[0]?.categoryname || "",
    });
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const handleDeleteCategory = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (categoryToDelete) {
      setDeleteLoading(true); // <-- set loading true
      try {
        await categoryAPI.deleteCategory(categoryToDelete.id);
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
        loadCategories();
        dispatch(fetchCategoryStatistics());
        toast.success("Category deleted successfully!");
      } catch (error) {
        toast.error("Failed to delete category.");
      } finally {
        setDeleteLoading(false); // <-- set loading false
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
    setDeleteLoading(false); // <-- reset loading
  };

  const handleSaveCategory = async (categoryData: CategoryFormData) => {
    try {
      setDialogOpen(false);
      loadCategories();
      dispatch(fetchCategoryStatistics()); // Only after add/edit
    } catch (error) {
      toast.error("Failed to save category.");
    }
  };

  const handleViewSubcategories = (category: Category) => {
    navigate(`/subcategories/${category.id}`);
  };

  const handleExportCategories = async () => {
    if (!Array.isArray(categories) || categories.length === 0) {
      toast.info("There is no category data to export");
      return;
    }
    try {
      const response = await categoryAPI.exportCategories();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "categories_export.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exported category data successfully");
    } catch (error) {
      toast.error("Failed to export categories");
    }
  };

  const handleImportCategories = () => {
    // TODO: implement import functionality
    toast.info("Import functionality is not yet implemented");
  };

  const handleDashboardShowToggle = async (category: Category) => {
    try {
      console.log("Toggling dashboard show for category:", category);
      // If isShow is true, send 0; if false, send 1
      const newIsShow = category.isShow === true ? 0 : 1;
      await categoryAPI.updateCategory(category.id, { isShow: newIsShow });
      toast.success(
        `Dashboard show set to ${newIsShow === 1 ? "Active" : "Inactive"}`
      );
      loadCategories();
    } catch (error) {
      toast.error("Failed to update dashboard show status.");
    }
  };

  const stats = [
    {
      title: "Total Categories",
      value: statistics.total,
      color: "primary" as const,
      loading: statisticsLoading,
    },
    {
      title: "Active Categories",
      value: statistics.active,
      color: "success" as const,
      loading: statisticsLoading,
    },
    {
      title: "Inactive Categories",
      value: statistics.deactive,
      color: "error" as const,
      loading: statisticsLoading,
    },
  ];

  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      {/* Removed skeleton early return to unify loader with ProductsList */}

      {/* Header Card */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: "#fff",
          boxShadow: "0 4px 18px -4px rgba(0,0,0,0.12)",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
            Categories
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage categories, view stats and export data.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
          <ExportImportButtons
            onExport={handleExportCategories}
            onImport={handleImportCategories}
          />
          <Button
            variant="contained"
            color="warning"
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: "0 4px 12px -2px rgba(0,0,0,0.18)",
              "&:hover": { boxShadow: "0 6px 16px -2px rgba(0,0,0,0.25)" },
            }}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards (neutral style) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            md: "repeat(3,1fr)",
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        {stats.map((stat, idx) => (
          <Card
            key={idx}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              background: "#fff",
              position: "relative",
              overflow: "hidden",
              "&:before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  stat.color === "primary"
                    ? "linear-gradient(120deg,rgba(25,118,210,.06),transparent)"
                    : stat.color === "success"
                    ? "linear-gradient(120deg,rgba(46,125,50,.08),transparent)"
                    : "linear-gradient(120deg,rgba(211,47,47,.08),transparent)",
                pointerEvents: "none",
              },
            }}
          >
            <CardContent sx={{ py: 2.5, px: 2.5 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  color: `${stat.color}.main`,
                }}
              >
                {stat.title}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                {stat.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Table */}
      <Box>
        <EnhancedTable
          columns={columns}
          data={categories || []}
          loading={loading}
          onView={handleViewSubcategories}
          onEdit={handleEditCategory}
          onDelete={handleDeleteCategory}
          showActions
          emptyMessage="No categories data found"
          showViewCondition={(row: Category) => row.categorystatus === 1}
          useExternalPagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          useExternalSorting
          onSort={handleSort}
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          onColumnReorder={handleColumnReorder}
          enableColumnReordering
          showStatusChangeCondition={(row: Category) =>
            row.categorystatus === 1 || row.categorystatus === 0
          }
          columnFilters={columnFilters}
          onColumnFilterChange={handleColumnFilterChange}
          showSelection={false}
          enableStickyFilters
          entityName="category"
        />
      </Box>

      {/* Category Dialog */}
      <CategoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveCategory}
        category={selectedCategory}
        mode={dialogMode}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading} // <-- pass loading state
        title="Delete Category"
        message="Are you sure you want to delete this category? All its subcategories and related products will also be deleted."
        itemName={categoryToDelete?.name || ""}
        itemType="category"
      />
    </Box>
  );
};

export default CategoriesList;
